import { vehiclePrices } from './vehiclePrices';
import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BYDBanner from './Asset/byd-sealion-7.webp';
import BYDBannerMobile from './Asset/byd-sealion-7-1stBanner-Mobile.jpg';
import SealionExterior1 from './Asset/byd-sealion-7-exterior-01.jpg';
import SealionExterior2 from './Asset/byd-sealion-7-exterior-02.jpg';
import SealionInterior5 from './Asset/byd-sealion-7-interior-05.jpg';
import SealionInterior11 from './Asset/byd-sealion-7-interior-11.jpg';
import SealionCharging15 from './Asset/byd-sealion-7-charging-15.jpg';
import SealionPerformance16 from './Asset/byd-sealion-7-performance-16.jpg';
import Sealion25 from './Asset/byd-sealion-7-v2l-25.jpg';
import brochure from './Asset/SEALION-7-BROCHURE.pdf';
import PageSeo, {
  absoluteUrl,
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from './PageSeo';

const sealionVariants = vehiclePrices.sealion7.map((item) => ({
  ...item,
  name: item.type,
  summary: 'NIK 2026 dengan baterai baru. Tersedia satu varian.',
}));

const buyingHighlights = [
  {
    title: 'Satu Varian NIK 2026',
    text: 'Pilihan Sealion 7 NIK 2026 kini tersedia satu varian seharga Rp879 juta.',
  },
  {
    title: 'Baterai Baru',
    text: 'Unit NIK 2026 menggunakan baterai baru. Konsultasikan kapasitas, jarak tempuh, dan pengisian daya untuk unit yang akan dibeli.',
  },
  {
    title: 'Blade Battery & Fast Charging',
    text: 'Teknologi baterai BYD memberi rasa aman, efisiensi tinggi, dan dukungan pengisian cepat DC.',
  },
  {
    title: 'V2L 3,3 kW',
    text: 'Gunakan daya mobil untuk perangkat outdoor, camping, acara keluarga, atau kebutuhan darurat.',
  },
];

const serviceAreas = [
  'BSD Serpong',
  'Gading Serpong',
  'Alam Sutera',
  'Bintaro',
  'Tangerang Selatan',
  'Jakarta Barat',
];

const sealionFaqs = [
  {
    question: 'Berapa harga BYD Sealion 7 di BYD BSD Serpong?',
    answer:
      'Harga BYD Sealion 7 NIK 2026 Rp879.000.000, satu varian dengan baterai baru. Hubungi Junius untuk cek promo dan simulasi cicilan terbaru.',
  },
  {
    question: 'Apa varian Sealion 7 NIK 2026 yang tersedia?',
    answer:
      'Sealion 7 NIK 2026 tersedia satu varian dengan baterai baru. Hubungi Junius untuk detail spesifikasi dan ketersediaan unit.',
  },
  {
    question: 'Apakah BYD Sealion 7 bisa test drive di BSD Serpong?',
    answer:
      'Bisa. Anda dapat menghubungi Junius untuk mengatur jadwal test drive BYD Sealion 7 di BYD BSD Serpong sesuai ketersediaan unit.',
  },
  {
    question: 'Apakah ada promo BYD Sealion 7?',
    answer:
      'Promo dapat berubah mengikuti periode dan ketersediaan unit. Cara paling cepat adalah chat Junius untuk cek promo, booking fee, dan estimasi pengiriman.',
  },
  {
    question: 'Apakah Sealion 7 cocok untuk keluarga?',
    answer:
      'Ya. Sealion 7 menawarkan kabin SUV yang luas, bagasi besar, fitur keselamatan modern, V2L, dan jarak tempuh yang nyaman untuk aktivitas keluarga.',
  },
];

function buildWhatsAppUrl(message) {
  return `https://api.whatsapp.com/send?phone=6281286864825&text=${encodeURIComponent(message)}`;
}

function JelajahiSealion() {
  const sections = useRef([]);
  const [visibleSections, setVisibleSections] = useState({});
  const isMobile = useMediaQuery('(max-width:768px)');

  const images = [
    {
      src: isMobile ? BYDBannerMobile : BYDBanner,
      title: 'BYD Sealion 7 BSD Serpong',
      subtitle:
        'Harga, promo, simulasi cicilan, dan test drive SUV listrik BYD bersama Junius.',
      isHero: true,
      specs: [
        { value: '2026', label: 'NIK' },
        { value: 'Baterai baru', label: 'Baterai' },
        { value: 'Rp879 Juta', label: 'Harga NIK 2026' },
      ],
    },
    {
      src: SealionExterior1,
      title: 'Desain Sporty Ocean X',
      description:
        'BYD Sealion 7 tampil sebagai SUV listrik premium dengan desain aerodinamis, garis bodi tegas, dan karakter sporty yang cocok untuk pemilik aktif di BSD Serpong dan Tangerang Selatan.',
    },
    {
      src: SealionExterior2,
      title: 'SUV Listrik untuk Harian dan Akhir Pekan',
      description:
        'Profil fastback, kap rendah, dan wheelbase panjang membuat Sealion 7 terlihat modern sekaligus tetap praktis untuk perjalanan keluarga, bisnis, dan mobilitas harian.',
    },
    {
      src: SealionInterior5,
      title: 'Interior Modern dan Nyaman',
      description:
        'Kabin Sealion 7 dirancang untuk memberi rasa luas, tenang, dan premium. Cocok untuk pengguna yang ingin SUV listrik dengan kenyamanan tinggi tanpa kehilangan karakter performa.',
    },
    {
      src: SealionInterior11,
      title: 'Bagasi Lega untuk Keluarga',
      description:
        'Ruang depan 58 liter dan bagasi belakang 520 liter dapat diperluas hingga 1.789 liter saat kursi belakang dilipat, sehingga lebih fleksibel untuk perjalanan jauh.',
    },
    {
      src: SealionCharging15,
      title: 'Charging Cepat dan Praktis',
      description:
        'Dukungan fast charging DC membantu mengurangi waktu tunggu. Untuk kebutuhan harian di Jabodetabek, Sealion 7 tetap praktis saat dipakai dari rumah, kantor, atau titik charging publik.',
    },
    {
      src: SealionPerformance16,
      title: 'BYD Sealion 7 NIK 2026',
      description:
        'SUV listrik untuk perjalanan harian dan kebutuhan keluarga. Tersedia satu varian NIK 2026 dengan baterai baru seharga Rp879 juta.',
    },
    {
      src: Sealion25,
      title: 'V2L 3,3 kW untuk Aktivitas Outdoor',
      description:
        'Fitur Vehicle-to-Load membuat Sealion 7 bisa menjadi sumber listrik untuk perangkat outdoor, acara keluarga, atau kebutuhan darurat.',
    },
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: sealionFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const vehicleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: 'BYD Sealion 7',
    brand: {
      '@type': 'Brand',
      name: 'BYD',
    },
    image: absoluteUrl(BYDBanner),
    description:
      'BYD Sealion 7 NIK 2026 adalah SUV listrik dengan baterai baru, tersedia satu varian seharga Rp879 juta.',
    offers: {
      '@type': 'AggregateOffer',
      url: absoluteUrl('/jelajahi-sealion-7'),
      priceCurrency: 'IDR',
      lowPrice: sealionVariants[0].priceValue,
      highPrice: sealionVariants[0].priceValue,
      offerCount: sealionVariants.length,
      availability: 'https://schema.org/InStock',
      seller: {
        '@id': 'https://bydbsdserpong.com/#dealer',
      },
    },
  };

  useEffect(() => {
    const currentSections = sections.current;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleSections((prev) => {
          const updated = { ...prev };
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              updated[entry.target.dataset.index] = true;
            }
          });
          return updated;
        });
      },
      { threshold: 0.35 },
    );

    currentSections.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      currentSections.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  return (
    <>
      <PageSeo
        title='BYD Sealion 7 NIK 2026 BSD Serpong | Baterai Baru Rp879 Juta'
        description='BYD Sealion 7 NIK 2026, satu varian dengan baterai baru seharga Rp879 juta. Konsultasi pembelian, promo, dan test drive bersama Junius di BYD BSD Serpong.'
        keywords={[
          ...keywordSets.localDealer,
          'harga BYD Sealion 7',
          'harga BYD Sealion 7 BSD Serpong',
          'promo BYD Sealion 7',
          'test drive BYD Sealion 7',
          'BYD Sealion 7 Tangerang Selatan',
          'BYD Sealion 7 baterai baru',
          'BYD Sealion 7 NIK 2026',
          'harga Sealion 7 879 juta',
          'sales BYD Sealion 7 Junius',
        ]}
        path='/jelajahi-sealion-7'
        image={BYDBanner}
        jsonLd={[
          productJsonLd({
            name: 'BYD Sealion 7',
            description:
              'SUV listrik BYD Sealion 7 NIK 2026 dengan baterai baru, satu varian seharga Rp879 juta.',
            image: BYDBanner,
            path: '/jelajahi-sealion-7',
            lowPrice: sealionVariants[0].priceValue,
            highPrice: sealionVariants[0].priceValue,
            offerCount: sealionVariants.length,
          }),
          vehicleJsonLd,
          faqJsonLd,
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'BYD Sealion 7', path: '/jelajahi-sealion-7' },
          ]),
        ]}
      />

      <Box component='main'>
        {images.map((img, index) => (
          <Box
            key={img.title || index}
            component='section'
            ref={(el) => (sections.current[index] = el)}
            data-index={index}
            aria-label={img.title || `BYD Sealion 7 section ${index + 1}`}
            sx={{
              position: 'relative',
              minHeight: { xs: img.isHero ? 620 : 560, md: '100vh' },
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              backgroundColor: '#111827',
            }}
          >
            <Box
              component='img'
              src={img.src}
              alt={
                img.isHero
                  ? 'BYD Sealion 7 BSD Serpong - SUV listrik BYD untuk test drive dan promo'
                  : `${img.title} BYD Sealion 7`
              }
              loading={index === 0 ? 'eager' : 'lazy'}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: img.isHero
                  ? 'linear-gradient(90deg, rgba(8,13,21,0.76) 0%, rgba(8,13,21,0.5) 44%, rgba(8,13,21,0.12) 100%)'
                  : 'linear-gradient(180deg, rgba(8,13,21,0.15) 0%, rgba(8,13,21,0.62) 100%)',
              }}
            />

            {img.isHero ? (
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  width: '100%',
                  maxWidth: 1180,
                  px: { xs: 2, md: 6 },
                  color: '#fff',
                }}
              >
                <Typography
                  sx={{
                    display: 'inline-flex',
                    px: 1.4,
                    py: 0.65,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(45,108,223,0.9)',
                    fontWeight: 900,
                    fontSize: '0.82rem',
                    textTransform: 'uppercase',
                  }}
                >
                  Harga BYD Sealion 7 BSD Serpong
                </Typography>
                <Typography
                  variant='h1'
                  component='h1'
                  sx={{
                    mt: 2,
                    maxWidth: 760,
                    fontWeight: 900,
                    lineHeight: 1,
                    fontSize: { xs: '2.45rem', sm: '3.4rem', md: '5rem' },
                  }}
                >
                  BYD Sealion 7 NIK 2026, Baterai Baru
                </Typography>
                <Typography
                  sx={{
                    mt: 2,
                    maxWidth: 650,
                    color: 'rgba(255,255,255,0.86)',
                    fontSize: { xs: '1rem', md: '1.15rem' },
                    lineHeight: 1.75,
                  }}
                >
                  {img.subtitle}
                </Typography>
                <Box
                  sx={{
                    mt: 3,
                    display: 'flex',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    component='a'
                    href={buildWhatsAppUrl(
                      'Halo Junius, saya mau tanya harga, promo, dan jadwal test drive BYD Sealion 7 di BYD BSD Serpong.',
                    )}
                    target='_blank'
                    rel='noopener noreferrer'
                    variant='contained'
                    sx={{
                      borderRadius: '8px',
                      px: 3,
                      py: 1.15,
                      fontWeight: 900,
                      textTransform: 'none',
                      backgroundColor: '#2d6cdf',
                      '&:hover': { backgroundColor: '#1f5fc8' },
                    }}
                  >
                    Chat Harga & Promo
                  </Button>
                  <Button
                    component={RouterLink}
                    to='/pricing'
                    variant='outlined'
                    sx={{
                      borderRadius: '8px',
                      px: 3,
                      py: 1.15,
                      fontWeight: 900,
                      textTransform: 'none',
                      color: '#fff',
                      borderColor: 'rgba(255,255,255,0.74)',
                      '&:hover': {
                        borderColor: '#fff',
                        backgroundColor: 'rgba(255,255,255,0.12)',
                      },
                    }}
                  >
                    Lihat Price List BYD
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  maxWidth: 900,
                  mx: 'auto',
                  px: { xs: 2, md: 4 },
                  color: '#fff',
                  textAlign: 'center',
                  transform: visibleSections[index]
                    ? 'translateY(0)'
                    : 'translateY(60px)',
                  opacity: visibleSections[index] ? 1 : 0,
                  transition: 'all 0.8s ease-in-out',
                }}
              >
                <Typography
                  component='h2'
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.08,
                    fontSize: { xs: '1.85rem', sm: '2.4rem', md: '3.4rem' },
                    textShadow: '0 2px 18px rgba(0,0,0,0.45)',
                  }}
                >
                  {img.title}
                </Typography>
                {img.description && (
                  <Typography
                    sx={{
                      mt: 2,
                      mx: 'auto',
                      maxWidth: 780,
                      color: 'rgba(255,255,255,0.88)',
                      lineHeight: 1.75,
                      fontSize: { xs: '0.96rem', md: '1.08rem' },
                      backgroundColor: 'rgba(8,13,21,0.46)',
                      borderRadius: '8px',
                      p: { xs: 2, md: 2.5 },
                    }}
                  >
                    {img.description}
                  </Typography>
                )}
                {img.src === Sealion25 && (
                  <Button
                    href={brochure}
                    download='SEALION-7-BROCHURE.pdf'
                    variant='outlined'
                    sx={{
                      mt: 3,
                      borderRadius: '8px',
                      borderColor: '#fff',
                      color: '#fff',
                      fontWeight: 900,
                      textTransform: 'none',
                      px: 3,
                      py: 1.1,
                      '&:hover': {
                        backgroundColor: '#fff',
                        color: '#111827',
                      },
                    }}
                  >
                    Download Brosur Sealion 7
                  </Button>
                )}
              </Box>
            )}

            {img.specs && (
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: 16, md: 72 },
                  right: { xs: 16, md: 72 },
                  bottom: { xs: 28, md: 48 },
                  zIndex: 1,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr 1fr 1fr',
                    md: 'repeat(3, 180px)',
                  },
                  gap: { xs: 1, md: 2 },
                }}
              >
                {img.specs.map((spec) => (
                  <Box
                    key={spec.label}
                    sx={{
                      p: { xs: 1.2, md: 2 },
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255,255,255,0.14)',
                      border: '1px solid rgba(255,255,255,0.26)',
                      color: '#fff',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '1rem', md: '1.5rem' },
                        fontWeight: 900,
                        lineHeight: 1,
                      }}
                    >
                      {spec.value}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.6,
                        fontSize: { xs: '0.72rem', md: '0.86rem' },
                      }}
                    >
                      {spec.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}

        <Box
          component='section'
          id='harga-sealion-7'
          aria-labelledby='harga-sealion-7-title'
          sx={{
            px: { xs: 2, md: 6 },
            py: { xs: 5, md: 8 },
            backgroundColor: '#fff',
          }}
        >
          <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
            <Typography
              id='harga-sealion-7-title'
              component='h2'
              sx={{
                fontWeight: 900,
                color: '#111827',
                lineHeight: 1.12,
                fontSize: { xs: '1.9rem', md: '2.6rem' },
              }}
            >
              Harga BYD Sealion 7 BSD Serpong
            </Typography>
            <Typography
              sx={{
                mt: 1.5,
                color: '#4b5563',
                lineHeight: 1.75,
                maxWidth: 820,
              }}
            >
              Sealion 7 NIK 2026 tersedia satu varian dengan baterai baru. Harga
              dapat berubah mengikuti program, stok, dan promo berjalan, jadi
              hubungi Junius untuk simulasi cicilan dan estimasi unit terbaru.
            </Typography>

            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2.5,
              }}
            >
              {sealionVariants.map((variant) => (
                <Box
                  key={variant.name}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: '8px',
                    border: '1px solid rgba(17,24,39,0.1)',
                    backgroundColor: '#f8fafc',
                    boxShadow: '0 14px 30px rgba(16,24,40,0.08)',
                  }}
                >
                  <Typography
                    component='h3'
                    sx={{ fontSize: '1.3rem', fontWeight: 900 }}
                  >
                    {variant.name}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      color: '#2d6cdf',
                      fontSize: { xs: '2rem', md: '2.35rem' },
                      fontWeight: 900,
                      lineHeight: 1,
                    }}
                  >
                    {variant.price}
                  </Typography>
                  <Typography
                    sx={{ mt: 1.2, color: '#4b5563', lineHeight: 1.6 }}
                  >
                    {variant.summary}
                  </Typography>
                  <Box sx={{ mt: 2.2, display: 'grid', gap: 1 }}>
                    {variant.details.map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                          py: 1,
                          borderBottom: '1px solid rgba(17,24,39,0.08)',
                        }}
                      >
                        <Typography sx={{ color: '#667085' }}>
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{ fontWeight: 900, textAlign: 'right' }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Button
                    component='a'
                    href={buildWhatsAppUrl(
                      `Halo Junius, saya mau minta simulasi cicilan dan promo BYD Sealion 7 ${variant.name}.`,
                    )}
                    target='_blank'
                    rel='noopener noreferrer'
                    variant='contained'
                    sx={{
                      mt: 2.5,
                      borderRadius: '8px',
                      width: '100%',
                      py: 1.15,
                      fontWeight: 900,
                      textTransform: 'none',
                      backgroundColor: '#11805a',
                      '&:hover': { backgroundColor: '#0d6a4b' },
                    }}
                  >
                    Minta Simulasi {variant.name}
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          component='section'
          aria-labelledby='alasan-sealion-7-title'
          sx={{
            px: { xs: 2, md: 6 },
            py: { xs: 5, md: 8 },
            backgroundColor: '#f5f7fa',
          }}
        >
          <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
            <Typography
              id='alasan-sealion-7-title'
              component='h2'
              sx={{
                fontWeight: 900,
                color: '#111827',
                lineHeight: 1.12,
                fontSize: { xs: '1.9rem', md: '2.6rem' },
              }}
            >
              Kenapa Banyak Pembeli Melirik Sealion 7?
            </Typography>
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr 1fr',
                  lg: 'repeat(4, 1fr)',
                },
                gap: 2,
              }}
            >
              {buyingHighlights.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    minHeight: 190,
                    p: 2.5,
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    border: '1px solid rgba(17,24,39,0.08)',
                  }}
                >
                  <Typography
                    component='h3'
                    sx={{ fontWeight: 900, fontSize: '1.1rem' }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{ mt: 1.2, color: '#4b5563', lineHeight: 1.65 }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          component='section'
          aria-labelledby='test-drive-sealion-7-title'
          sx={{
            px: { xs: 2, md: 6 },
            py: { xs: 5, md: 8 },
            backgroundColor: '#fff',
          }}
        >
          <Box
            sx={{
              maxWidth: 1180,
              mx: 'auto',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '0.95fr 1.05fr' },
              gap: { xs: 3, md: 5 },
              alignItems: 'center',
            }}
          >
            <Box
              component='img'
              src={SealionPerformance16}
              alt='Test drive BYD Sealion 7 di BYD BSD Serpong'
              loading='lazy'
              sx={{
                width: '100%',
                minHeight: { xs: 260, md: 420 },
                objectFit: 'cover',
                borderRadius: '8px',
              }}
            />
            <Box>
              <Typography
                id='test-drive-sealion-7-title'
                component='h2'
                sx={{
                  fontWeight: 900,
                  color: '#111827',
                  lineHeight: 1.12,
                  fontSize: { xs: '1.9rem', md: '2.6rem' },
                }}
              >
                Test Drive BYD Sealion 7 di BYD BSD Serpong
              </Typography>
              <Typography sx={{ mt: 1.5, color: '#4b5563', lineHeight: 1.75 }}>
                Booking test drive Sealion 7 bersama Junius untuk merasakan
                langsung akselerasi, kabin, fitur V2L, posisi mengemudi, dan
                kenyamanan suspensi. Area konsultasi meliputi:
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {serviceAreas.map((area) => (
                  <Box
                    key={area}
                    sx={{
                      px: 1.4,
                      py: 0.7,
                      borderRadius: '8px',
                      backgroundColor: '#edf4ff',
                      color: '#1f5fc8',
                      fontWeight: 900,
                      fontSize: '0.88rem',
                    }}
                  >
                    {area}
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <Button
                  component='a'
                  href={buildWhatsAppUrl(
                    'Halo Junius, saya mau booking test drive BYD Sealion 7 di BYD BSD Serpong.',
                  )}
                  target='_blank'
                  rel='noopener noreferrer'
                  variant='contained'
                  sx={{
                    borderRadius: '8px',
                    px: 3,
                    py: 1.15,
                    textTransform: 'none',
                    fontWeight: 900,
                    backgroundColor: '#2d6cdf',
                    '&:hover': { backgroundColor: '#1f5fc8' },
                  }}
                >
                  Booking Test Drive
                </Button>
                <Button
                  component={RouterLink}
                  to='/blog/sealion-7-suv-listrik-sporty'
                  variant='outlined'
                  sx={{
                    borderRadius: '8px',
                    px: 3,
                    py: 1.15,
                    textTransform: 'none',
                    fontWeight: 900,
                    borderColor: '#2d6cdf',
                    color: '#2d6cdf',
                    '&:hover': {
                      borderColor: '#1f5fc8',
                      backgroundColor: '#edf4ff',
                    },
                  }}
                >
                  Baca Review Sealion 7
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          component='section'
          aria-labelledby='faq-sealion-7-title'
          sx={{
            px: { xs: 2, md: 6 },
            py: { xs: 5, md: 8 },
            backgroundColor: '#f5f7fa',
          }}
        >
          <Box sx={{ maxWidth: 960, mx: 'auto' }}>
            <Typography
              id='faq-sealion-7-title'
              component='h2'
              sx={{
                fontWeight: 900,
                color: '#111827',
                lineHeight: 1.12,
                fontSize: { xs: '1.9rem', md: '2.6rem' },
                mb: 2.5,
              }}
            >
              FAQ BYD Sealion 7
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {sealionFaqs.map((faq) => (
                <Box
                  key={faq.question}
                  component='details'
                  sx={{
                    p: 2.2,
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    border: '1px solid rgba(17,24,39,0.08)',
                    '& summary': {
                      cursor: 'pointer',
                      fontWeight: 900,
                      color: '#111827',
                    },
                  }}
                >
                  <Box component='summary'>{faq.question}</Box>
                  <Typography
                    sx={{ mt: 1.2, color: '#4b5563', lineHeight: 1.7 }}
                  >
                    {faq.answer}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          component='section'
          sx={{
            px: { xs: 2, md: 6 },
            py: { xs: 5, md: 8 },
            backgroundColor: '#111827',
            color: '#fff',
            textAlign: 'center',
          }}
        >
          <Box sx={{ maxWidth: 880, mx: 'auto' }}>
            <Typography
              component='h2'
              sx={{
                fontWeight: 900,
                lineHeight: 1.12,
                fontSize: { xs: '1.9rem', md: '2.7rem' },
              }}
            >
              Siap Cek Unit, Promo, dan Jadwal Test Drive Sealion 7?
            </Typography>
            <Typography
              sx={{
                mt: 1.5,
                color: 'rgba(255,255,255,0.78)',
                lineHeight: 1.75,
              }}
            >
              Hubungi Junius untuk info harga BYD Sealion 7 terbaru, simulasi
              cicilan, ketersediaan warna, promo, dan jadwal test drive di BYD
              BSD Serpong.
            </Typography>
            <Button
              component='a'
              href={buildWhatsAppUrl(
                'Halo Junius, saya mau cek harga terbaru, promo, warna, dan jadwal test drive BYD Sealion 7.',
              )}
              target='_blank'
              rel='noopener noreferrer'
              variant='contained'
              sx={{
                mt: 3,
                borderRadius: '8px',
                px: 3.5,
                py: 1.2,
                textTransform: 'none',
                fontWeight: 900,
                backgroundColor: '#11805a',
                '&:hover': { backgroundColor: '#0d6a4b' },
              }}
            >
              Hubungi Junius via WhatsApp
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default JelajahiSealion;
